<template>
  <div class="configuration-tab">
    <app-loading :loading="isLoading" is-background />

    <ValidationObserver v-if="!isLoading" v-slot="v">
      <b-form id="edit-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
        <div class="form-container">
          <!-- Basic Details -->
          <basic-details-section
            :name="awardForm.name"
            :scope="scope"
            :scope-fields="scopeFields"
            @update:name="awardForm.name = $event"
            @update:scope="onScopeChange"
          >
            <template #after-divisions>
              <div class="milestone-event-type-container">
                <div v-b-tooltip.hover.bottom="milestoneLockHint" class="milestone-event-type-input">
                  <app-input
                    v-model="milestoneEventType"
                    :validate-immediate="false"
                    name="Milestone type"
                    type="dropdown"
                    :options="milestoneOptions"
                    undefined-label="Select type"
                    rules="required"
                    :disabled="hasBeenAwarded"
                  >
                    <template #label>
                      <div class="label">
                        Milestone type
                        <small>{{ milestoneLockHint }}</small>
                      </div>
                    </template>
                  </app-input>
                </div>
              </div>

              <div v-if="milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY">
                <app-input
                  v-model="anniversaryYear"
                  :validate-immediate="false"
                  name="Anniversary year"
                  type="number"
                  placeholder="e.g. 5"
                  append="years"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  step="1"
                  :rules="
                    milestoneEventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY
                      ? 'required|min_value:1|max_value:50'
                      : ''
                  "
                  class="anniversary-year-input m-0"
                  :disabled="hasBeenAwarded"
                >
                  <template #label>
                    <div class="label">
                      Anniversary year
                      <small>{{ milestoneLockHint }}</small>
                    </div>
                  </template>
                </app-input>
              </div>

              <app-input
                v-model="recipientMessage"
                :validate-immediate="false"
                name="Recipient message"
                type="textarea"
                :rows="2"
                :maxlength="1000"
                rules="required|max:1000"
                :description="recipientMessageDescription"
                placeholder="e.g. Congratulation on your 5 year anniversary with the company!"
                @input="onRecipientMessageInput"
              >
                <template #label>
                  <div class="label">
                    Add a message
                    <small>Write a default message that celebrates the milestone.</small>
                  </div>
                </template>
              </app-input>
            </template>
          </basic-details-section>

          <!-- Appearance -->
          <image-upload-section
            :banner-images="bannerImages"
            :banner-images-error="bannerImagesError"
            :badge-file="badgeFile"
            :badge-preview-url="badgePreviewUrl"
            :allowed-image-types="allowedImageTypes"
            :max-size-bytes="MAX_BANNER_SIZE_BYTES"
            @add:bannerImages="addBannerImages"
            @add:pendingBannerFiles="addPendingBannerFiles"
            @remove:bannerImage="removeBannerImage"
            @update:badgeFile="onBadgeFileUpdate"
            @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
            @gallerySelect:badge="onBadgeGallerySelect"
            @remove:badge="onBadgeRemove"
          />

          <!-- Award Details -->
          <div>
            <h4>Award details</h4>
            <sme-card large>
              <div class="card-container">
                <div class="label-row">
                  <div class="label">
                    Enable badges (optional)
                    <small>Turn awards into collectible badges that appear on recipients' home page.</small>
                  </div>
                  <b-form-checkbox v-model="badgesEnabled" switch />
                </div>

                <monetary-reward-section
                  :enabled="monetaryRewardEnabled"
                  :currency-symbol="currencySymbol"
                  @update:enabled="monetaryRewardEnabled = $event"
                  :amounts="awardValueMinor"
                  :amount-error="awardValueError"
                  :redemption-options="selectedRedemptionOptions"
                  :redemption-error="redemptionSelectionError"
                  :expiration-months="expirationMonths"
                  @update:amounts="awardValueMinor = $event"
                  @update:redemptionOptions="selectedRedemptionOptions = $event"
                  @update:expirationMonths="expirationMonths = $event"
                >
                  <template #reward-amounts>
                    <div class="reward-amount-container">
                      <app-input
                        v-model="singleAwardValue"
                        :validate-immediate="false"
                        name="Reward amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        inputmode="decimal"
                        :prepend="currencySymbol"
                        class="amount-input"
                        @input="onAmountInput($event)"
                        @keydown="onAmountKeydown"
                        @blur="onAmountBlur()"
                        @change="onAmountBlur()"
                      />
                    </div>
                  </template>
                </monetary-reward-section>
              </div>
            </sme-card>
          </div>
        </div>

        <!-- Footer -->
        <div class="form-footer">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="outline-secondary" class="mr-2" :disabled="saving" @click="resetForm"> Reset </b-button>
          <b-button type="submit" variant="primary" :disabled="saving || !hasChanges">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Saving...' : 'Save changes' }}
          </b-button>
        </div>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from 'vue';
import ApiClient from '@/ApiClient';
import {
  RECOGNITION_APPROVAL_TYPES,
  RECOGNITION_MILESTONE_EVENT_OPTIONS,
  RECOGNITION_MILESTONE_EVENT_TYPES,
  MILESTONE_LOCKED_REASON,
  MILESTONE_EDITABLE_HINT,
  toTwoDecimalsOrEmpty,
  programScope,
} from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';

const props = defineProps({
  program: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  scope,
  scopeFields,
  programScopePayload,
  scopeSnapshot,
  monetaryRewardEnabled,
  awardValueMinor,
  awardValueError,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  bannerImages,
  bannerImagesError,
  addBannerImages,
  addPendingBannerFiles,
  removeBannerImage,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBadgeGallerySelect,
  onBadgeRemove,
  badgeS3Key,
  saving,
  errorMessage,
  sanitizeAmountInput,
  majorToMinor,
  onAmountKeydown,
  loadScopeFields,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  getImageData,
  getGrantExpiryInterval,
  validateRedemptionOptions,
} = useAwardForm();

// Milestone-specific state
const badgesEnabled = ref(true);
const milestoneEventType = ref(undefined);
const milestoneOptions = RECOGNITION_MILESTONE_EVENT_OPTIONS;
const anniversaryYear = ref('');
const recipientMessage = ref('');
const singleAwardValue = ref(undefined);
const isLoading = ref(true);

const hasBeenAwarded = computed(() => Boolean(props.program?.has_been_awarded));
const milestoneLockHint = computed(() => (hasBeenAwarded.value ? MILESTONE_LOCKED_REASON : MILESTONE_EDITABLE_HINT));

// Track original values for change detection
const originalValues = ref({});

const { doToast, TOAST_VARIANT } = useToast();

const recipientMessageDescription = computed(() => {
  const len = String(recipientMessage.value || '').length;
  return `${len}/1000 characters`;
});

// Detect if form has changes
const hasChanges = computed(() => {
  const current = getCurrentFormState();
  return JSON.stringify(current) !== JSON.stringify(originalValues.value);
});

/**
 * Parse ISO 8601 duration to months.
 */
const parseGrantExpiryInterval = interval => {
  if (!interval) {
    return '';
  }
  const str = String(interval);
  const monthMatch = str.match(/^P(\d+)M$/);
  if (monthMatch) {
    return monthMatch[1];
  }
  const dayMatch = str.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1], 10);
    const months = Math.round(days / 30);
    return months > 0 ? String(months) : '';
  }
  return '';
};

/**
 * Get current form state for comparison
 */
const getCurrentFormState = () => ({
  name: awardForm.value.name,
  scope: scopeSnapshot.value,
  milestoneEventType: milestoneEventType.value,
  anniversaryYear: anniversaryYear.value,
  recipientMessage: recipientMessage.value,
  badgesEnabled: badgesEnabled.value,
  monetaryRewardEnabled: monetaryRewardEnabled.value,
  singleAwardValue: singleAwardValue.value,
  selectedRedemptionOptions: [...(selectedRedemptionOptions.value || [])].sort(),
  expirationMonths: expirationMonths.value,
  badgeFileName: badgeFile.value?.name ?? null,
  bannerImageKeys: bannerImages.value.map(i => i.s3Key).join(','),
  badgeS3Key: badgeS3Key.value,
});

/**
 * Populate form from program data
 */
const populateFromProgram = program => {
  if (!program) {
    return;
  }

  // Basic details
  awardForm.value.name = program.program_name || '';
  badgesEnabled.value = program.show_badge !== false;
  scope.value = programScope(program);

  // Milestone config
  const milestone = program.properties?.milestone || {};
  milestoneEventType.value = milestone.event_type || undefined;
  anniversaryYear.value = milestone.anniversary_year ? String(milestone.anniversary_year) : '';
  recipientMessage.value = milestone.recipient_message || '';

  // Images - set preview URLs and S3 keys from existing images
  const bannerKeys = program.banner_s3_keys || (program.banner_s3_key ? [program.banner_s3_key] : []);
  const bannerUrls =
    program.banner_presigned_urls || (program.banner_presigned_url ? [program.banner_presigned_url] : []);
  bannerImages.value = bannerKeys.map((s3Key, i) => ({ id: s3Key, s3Key, file: null, url: bannerUrls[i] || '' }));
  if (program.badge_presigned_url) {
    badgePreviewUrl.value = program.badge_presigned_url;
  } else {
    badgePreviewUrl.value = '';
  }
  badgeS3Key.value = program.badge_s3_key || '';
  // Clear any previously selected files
  onBadgeFileUpdate(null);

  // Monetary reward — infer the toggle from the stored amount. Badge-only
  // milestones (award_value_minor null/missing) load with the toggle off.
  const valueMinor = program.properties?.award_value_minor;
  monetaryRewardEnabled.value = Number.isFinite(valueMinor);
  if (Number.isFinite(valueMinor)) {
    singleAwardValue.value = (valueMinor / 100).toFixed(2);
  } else {
    singleAwardValue.value = undefined;
  }

  // Redemption options
  const validGrantTypes = program.properties?.valid_grant_types || ['VOUCHER'];
  selectedRedemptionOptions.value = validGrantTypes;

  // Expiration
  expirationMonths.value = parseGrantExpiryInterval(program.grant_expiry_interval);

  // Store original values for change detection
  originalValues.value = getCurrentFormState();
};

/**
 * Reset form to original program values
 */
const resetForm = () => {
  populateFromProgram(props.program);
  // Clear any newly selected image files
  onBadgeFileUpdate(null);
  errorMessage.value = '';
  awardValueError.value = '';
  redemptionSelectionError.value = '';
};

const buildProperties = redemptionMethods => {
  // API requires at least 1 valid_grant_type - default to VOUCHER if none provided
  const grantTypes = (redemptionMethods || []).map(String);
  const props = {
    valid_grant_types: grantTypes.length > 0 ? grantTypes : ['VOUCHER'],
    approvers: undefined,
  };

  // When monetary rewards are disabled, explicitly set fields to null to clear them
  if (!monetaryRewardEnabled.value) {
    props.award_value_minor = null;
    props.default_award_value_minor = null;
  } else if (singleAwardValue.value !== undefined && singleAwardValue.value !== null) {
    const minor = majorToMinor(singleAwardValue.value);
    if (Number.isFinite(minor)) {
      props.award_value_minor = minor;
      props.default_award_value_minor = minor;
    }
  }

  if (milestoneEventType.value) {
    const eventType = String(milestoneEventType.value);
    const milestone = { event_type: eventType };
    if (eventType === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
      const yearInt = parseInt(String(anniversaryYear.value || '').trim(), 10);
      if (Number.isFinite(yearInt)) {
        milestone.anniversary_year = yearInt;
      }
    }
    milestone.recipient_message = String(recipientMessage.value || '').slice(0, 1000);
    props.milestone = milestone;
  }

  return props;
};

const buildPayload = properties => ({
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.NONE,
  show_badge: badgesEnabled.value,
  ...programScopePayload.value,
  grant_expiry_interval: getGrantExpiryInterval(),
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];

  try {
    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    // Require a valid single award value when monetary rewards are enabled
    if (monetaryRewardEnabled.value) {
      const minor = majorToMinor(singleAwardValue.value);
      if (!Number.isFinite(minor) || minor <= 0) {
        awardValueError.value = 'Please enter a valid reward amount.';
        saving.value = false;
        return;
      }
      awardValueError.value = '';
    }

    const properties = buildProperties(redemptionMethods);
    const payload = buildPayload(properties);
    const { files, s3Keys } = getImageData();

    await ApiClient.updateRecognitionProgram(props.program.recognition_award_program_id, payload, files, s3Keys);

    doToast('Success', 'Award updated successfully', TOAST_VARIANT.SUCCESS);
    originalValues.value = getCurrentFormState();
    emit('updated');
  } catch (e) {
    errorMessage.value = e?.response?.data?.message || e?.variables?.message || e?.message || 'Failed to update award';
  } finally {
    saving.value = false;
  }
};

const onScopeChange = newScope => {
  scope.value = newScope;
};

const onAmountInput = value => {
  singleAwardValue.value = sanitizeAmountInput(value);
};

const onAmountBlur = () => {
  singleAwardValue.value =
    singleAwardValue.value === '' || singleAwardValue.value === undefined
      ? ''
      : toTwoDecimalsOrEmpty(singleAwardValue.value);
};

const onRecipientMessageInput = value => {
  recipientMessage.value = String(value ?? '').slice(0, 1000);
};

watch(milestoneEventType, val => {
  if (val !== RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
    anniversaryYear.value = '';
  }
});

watch(singleAwardValue, newVal => {
  if (!monetaryRewardEnabled.value) {
    awardValueError.value = '';
    return;
  }
  const minor = majorToMinor(newVal);
  if (Number.isFinite(minor) && awardValueError.value) {
    awardValueError.value = '';
  }
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
    singleAwardValue.value = undefined;
  }
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await loadScopeFields();
    setupCommonWatchers();
    populateFromProgram(props.program);
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => props.program,
  newProgram => {
    if (newProgram) {
      populateFromProgram(newProgram);
    }
  },
);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

.configuration-tab {
  position: relative;
  min-height: 200px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

/* Once awarded the milestone type is locked, and a white field reads as editable. The
   shared dropdown-control styles keep disabled toggles white, so grey this one back. */
.milestone-event-type-input :deep(.dropdown-control__toggle[disabled]) {
  background-color: var(--palette-color-default-lighten-90) !important;
  opacity: 1;
}
</style>
